import {
  endAlignment,
  ErrorMessage,
  inputContainerStyle,
  InputStyled,
} from "./contactForm";
import { Squarebtn } from "../../common/squareBtnProps";
import { useNavigate } from "react-router-dom";
import { AuthFormProps, RegFormData, RegFormProps } from "../../common/types";
import {
  handleRegBlur,
  regValidate,
  regValidateField,
} from "../../../utils/Validations/regAuthFormValidations";
import { useRef, useState } from "react";
import { useAlert } from "../../../hooks/useAlert";
import handleLoginCreateFormSubmit from "../../../utils/FormSubmit/loginAndCreateFormSubmit";
import Loader from "../Loader";
import { useAuth } from "../../../context/AuthContext";
import PasswordInput from "../../../utils/CustomizedLibraries/customPasswordInput";
import { CenterForm } from "../../common/FormLayouts";
import { useTheme as styledTheme } from "styled-components";
import { useRippleEffect } from "../../../hooks/useRippleEffect";
import { OTPAndBtn } from "./ResetPasswordForms";
import RequestOTPButton from "../../../utils/FormSubmit/requestOTP";
import { useLoader } from "../../../hooks/useLoader";

export const RegForm: React.FC<RegFormProps> = ({
  isLogin,
  regFormData,
  errors,
  touched,
  setRegFormData,
  setTouched,
  setErrors,
}) => {
  const navigate = useNavigate();
  const { setShowLoader, LoaderComponent } = useLoader(); // Loader state
  const { showAlert, closeAlert } = useAuth(); //global alert
  const { setAccessToken } = useAuth();
  const styledComponentTheme = styledTheme();
  const loginRefBtn = useRef<HTMLButtonElement>(null);
  //ripple effect
  useRippleEffect(loginRefBtn);

  const handleLoginNav = () => {
    navigate("/dashboard/login");
  };

  // Handle input changes
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    if (name === "otp") {
      // Restrict to 6 digits
      const sanitizedValue = value.replace(/[^0-9]/g, "");
      if (sanitizedValue.length > 6) return; // Restrict to 6 digits

      setRegFormData((prev) => ({
        ...prev,
        otp: value, // Ensure 'otp code' is always a string of digits
      }));
    }

    setRegFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // console.log("Input Value", value);
    validateFieldWrapper(name as keyof RegFormData, value);
  };

  const handleBlurWrapper = (name: keyof RegFormData, value: string) =>
    handleRegBlur(name, value, touched, setTouched, validateFieldWrapper);

  const validateFieldWrapper = (name: keyof RegFormData, value: string) => {
    regValidateField(name, value, errors, setErrors, regFormData);
  };

  const validateWrapper = () =>
    regValidate(regFormData, errors, setErrors, setTouched);

  const handleCreateAccount = (e: React.FormEvent) => {
    e.preventDefault();
    handleLoginCreateFormSubmit(
      regFormData,
      setRegFormData,
      {
        setTouched,
        validateWrapper,
        showAlert,
        showLoader: setShowLoader,
        setAccessToken,
        closeAlert,
      },
      isLogin,
      navigate
    );
  };

  return (
    <>
      <CenterForm>
        <form onSubmit={handleCreateAccount} className="formStyle">
          <div style={inputContainerStyle}>
            <label htmlFor="email">Email</label>
            <InputStyled
              type="email"
              id="email"
              autoComplete="email"
              name="email"
              placeholder="Enter your email"
              value={regFormData.email}
              onChange={handleChange}
              onBlur={() => handleBlurWrapper("email", regFormData.email ?? "")}
              $errorColor={errors.email ? "red" : ""}
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </div>

          <div style={inputContainerStyle}>
            <label htmlFor="name">Name</label>
            <InputStyled
              type="text"
              id="name"
              autoComplete="name"
              name="name"
              placeholder="Enter your first and second name"
              value={regFormData.name}
              onChange={handleChange}
              onBlur={() => handleBlurWrapper("name", regFormData.name ?? "")}
              $errorColor={errors.name ? "red" : ""}
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </div>

          <PasswordInput
            id={"password"}
            name={"password"}
            label={"Password"}
            placeholder={"Enter your password"}
            value={regFormData.password}
            onChange={handleChange}
            onBlurWrapper={handleBlurWrapper}
            error={errors.password}
          />
          <PasswordInput
            id={"confirm-password"}
            name={"confirmPassword"}
            label={"Confirm password"}
            placeholder={"Confirm your password"}
            value={regFormData.confirmPassword}
            onChange={handleChange}
            onBlurWrapper={handleBlurWrapper}
            error={errors.confirmPassword}
          />

          <OTPAndBtn style={inputContainerStyle}>
            <div style={{ width: "48%" }}>
              <InputStyled
                className="no-spinner"
                type="number"
                id="code-forgot-password"
                autoComplete="one-time-code"
                name="otp"
                placeholder="OTP code"
                value={regFormData.otp}
                onChange={handleChange}
                onBlur={() => handleBlurWrapper("otp", regFormData.otp ?? "")}
                onKeyDown={(e) => {
                  if (e.key === "e" || e.key === "E" || e.key === ".") {
                    e.preventDefault();
                  }
                }}
                $errorColor={errors.otp ? "red" : ""}
              />
              {errors.otp && <ErrorMessage>{errors.otp}</ErrorMessage>}
            </div>
            <RequestOTPButton<RegFormData>
              email={regFormData.email}
              showAlert={showAlert}
              closeAlert={closeAlert}
              handleChange={handleChange} // Pass handleChange
              validateFieldWrapper={validateFieldWrapper} // Pass validation function
              errors={errors} // Pass errors object
              purpose="email_verification"
            />
          </OTPAndBtn>

          <div style={endAlignment}>
            <Squarebtn
              ref={loginRefBtn}
              type="submit"
              height="50px"
              width="120px"
              $bacgrndColor={styledComponentTheme.text}
              $color={styledComponentTheme.circularIconTextBg}
            >
              Create
            </Squarebtn>
          </div>
          <p className="questionText" onClick={handleLoginNav}>
            Already have an account?
            <span> Sign in</span>
          </p>
        </form>
      </CenterForm>

      {/* Show Loader while submitting */}
      {LoaderComponent}
    </>
  );
};

export const AuthForm: React.FC<AuthFormProps> = ({
  isLogin,
  authFormData,
  setAuthFormData,
}) => {
  const navigate = useNavigate();
  const [showLoader, setshowLoader] = useState(false); // Loader state
  const { showAlert, closeAlert, AlertComponent } = useAlert();
  const {
    setAccessToken,
    setIsLoggedOut,
    SESSION_KEY,
    setLoggedInUserName,
    logout,
    refreshAccessToken,
  } = useAuth();
  const styledComponentTheme = styledTheme();
  const loginRefBtn = useRef<HTMLButtonElement>(null);
  //ripple effect
  useRippleEffect(loginRefBtn);

  const handleSignUpNav = () => {
    navigate("/dashboard/register");
  };

  const handleResetPasswordNav = () => {
    navigate("/dashboard/forgot-password");
  };

  // Handle input changes
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setAuthFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    handleLoginCreateFormSubmit(
      authFormData,
      setAuthFormData,
      {
        showAlert,
        showLoader: setshowLoader,
        setAccessToken,
        closeAlert,
        setIsLoggedOut,
        SESSION_KEY,
        setLoggedInUserName,
        logout,
        refreshAccessToken,
      },
      isLogin, // isLogin is true for login
      navigate
    );
  };

  return (
    <>
      <CenterForm>
        <form onSubmit={handleLogin} className="formStyle">
          <div style={inputContainerStyle}>
            <label htmlFor="email">Email</label>
            <InputStyled
              type="email"
              id="email"
              autoComplete="email"
              name="email"
              placeholder="Enter your email"
              value={authFormData.email}
              onChange={handleChange}
              $errorColor={false ? "red" : ""}
            />
          </div>

          <PasswordInput
            id={"password"}
            name={"password"}
            label={"Password"}
            placeholder={"Enter your password"}
            value={authFormData.password}
            onChange={handleChange}
          />

          <div style={{ display: "flex" }}>
            <p style={{ cursor: "pointer" }} onClick={handleResetPasswordNav}>
              Forgot password?
            </p>
          </div>

          <div style={endAlignment}>
            <Squarebtn
              ref={loginRefBtn}
              type="submit"
              height="50px"
              width="120px"
              $bacgrndColor={styledComponentTheme.text}
              $color={styledComponentTheme.circularIconTextBg}
            >
              Sign in
            </Squarebtn>
          </div>
          <p className="questionText" onClick={handleSignUpNav}>
            Don't have an account?
            <span> Create</span>
          </p>
        </form>
      </CenterForm>

      {/* Call smart alert component here*/}
      {AlertComponent}

      {/* Show Loader while submitting */}
      {showLoader && <Loader />}
    </>
  );
};
